import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {useMutation} from "@khan/data-gql";
import {AI_GUIDE_RESET_LANGUAGE_PREFERENCES_MUTATION} from "@khan/guide-core";
import {Errors, KAError} from "@khan/logging";

import {SET_BASIC_SETTINGS_MUTATION} from "./basic-settings-mutation.ts";
import {Section} from "./components/section/section.tsx";
import LanguageSelector from "./language-selector.tsx";
import {SaveChangesButton} from "./save-changes-button.tsx";

interface Props {
    initialUserKaLocale: string | "unspecified";
    kaid: string;
    successfullyChangedSettings: (
        userKaLocale: string | "unspecified",
    ) => unknown;
}

const LanguageSettings: React.FC<Props> = ({
    initialUserKaLocale,
    kaid,
    successfullyChangedSettings,
}) => {
    const [userKaLocale, setUserKaLocale] = React.useState(initialUserKaLocale);
    const [setBasicSettingsMutation, result] = useMutation(
        SET_BASIC_SETTINGS_MUTATION,
    );
    const [resetGuideLanguageMutation] = useMutation(
        AI_GUIDE_RESET_LANGUAGE_PREFERENCES_MUTATION,
    );

    const storeSettingsAndModifyState = async () => {
        try {
            await setBasicSettingsMutation({userLanguage: userKaLocale, kaid});
            // Whenever we change the user preferred language we also clear
            // Khanmigo guide language in order to ensure it will default to
            // the user preferred. This is required because in Classic Khan
            // the guide preferrences language is separately controlled,
            // and may be different from the user settings preferred language.
            // Khan Classroom has no separate control for Khanmigo language,
            // instead it is driven from user primary language selection.
            await resetGuideLanguageMutation();
        } catch (e) {
            throw new KAError(
                "Failed to set basic settings for language",
                Errors.Internal,
                {
                    cause: e as Error,
                },
            );
        }

        successfullyChangedSettings(userKaLocale);
    };

    return (
        <View
            testId="language-settings"
            style={{gap: sizing.size_160}}
            role="region"
            aria-label={t`Language settings`}
        >
            <Section
                header={t`Language settings`}
                children={
                    <View style={styles.languageSelectorWrapper}>
                        <LanguageSelector
                            userLanguage={userKaLocale}
                            onChange={(userKaLocale) =>
                                setUserKaLocale(userKaLocale)
                            }
                            disabled={result?.status === "loading"}
                        />
                    </View>
                }
                footer={
                    <SaveChangesButton
                        setSettings={storeSettingsAndModifyState}
                        isLoading={result?.status === "loading"}
                        isDisabled={initialUserKaLocale === userKaLocale}
                        hasUnexpectedError={result?.status === "error"}
                    />
                }
            />
        </View>
    );
};

const styles = StyleSheet.create({
    languageSelectorWrapper: {
        maxWidth: 400,
        width: "100%",
    },
});

export default React.memo(LanguageSettings);
